import { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Upload, Buildings, ClockCounterClockwise, ShieldCheck, ArrowsClockwise, X, Plus } from "@phosphor-icons/react";

/**
 * NetWorthV2AdminPanel — admin Control Room tab consolidating:
 *   • AVM-Lite benchmark CSV upload (Enh 1)
 *   • Property History feature toggle + Karza/digiverification API key (Phase B)
 *   • Recent property-history search log
 *
 * Lives under /app/admin → Control room → Net worth & history.
 */
export default function NetWorthV2AdminPanel() {
  const [tab, setTab] = useState("avm");
  return (
    <div className="space-y-4" data-testid="networth-v2-admin-panel">
      <div>
        <div className="eyebrow inline-flex items-center gap-2">
          <Buildings size={12} weight="duotone" /> Net worth & property history
        </div>
        <p className="text-xs text-muted-foreground mt-1 max-w-2xl">
          Upload real RERA/NoBroker market benchmarks (replaces conservative defaults),
          enable / disable the 40-year Property History feature, and paste your Karza or
          digiverification API key when ready to go live.
        </p>
      </div>

      <DigestWidget />

      <div className="inline-flex border hairline rounded overflow-hidden text-xs flex-wrap">
        {[
          ["avm", "AVM benchmarks"],
          ["lenders", "Lenders (quarterly)"],
          ["cii", "CII (annual)"],
          ["history", "Property history"],
          ["searches", "Recent searches"],
        ].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)}
            data-testid={`nwv2-admin-tab-${id}`}
            className={`px-3 py-1.5 ${tab === id ? "bg-[hsl(var(--primary))] text-white" : "hover:bg-muted/40"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "avm"      && <AvmTab />}
      {tab === "lenders"  && <LendersTab />}
      {tab === "cii"      && <CiiTab />}
      {tab === "history"  && <PropertyHistoryTab />}
      {tab === "searches" && <SearchesTab />}
    </div>
  );
}

/* ---------------------- Lender refresh (quarterly) ----------------------- */
function LendersTab() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState([]);
  const [asOf, setAsOf] = useState("");
  const [saving, setSaving] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(null);
  const [autoBusy, setAutoBusy] = useState(false);
  const [autoSel, setAutoSel] = useState(new Set());
  const load = async () => {
    try {
      const { data } = await api.get("/admin/calc/lenders");
      setData(data);
      setDraft((data.lenders || []).map((l) => ({ ...l })));
      setAsOf(data.as_of || "");
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);
  const updateRow = (i, k, v) => {
    setDraft((arr) => arr.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  };
  const removeRow = (i) => setDraft((arr) => arr.filter((_, idx) => idx !== i));
  const addRow = () => setDraft((arr) => [...arr, {
    id: "", name: "", rate: 9.0, foir: 0.5, ltv: 0.8, fee_pct: 0.005, perks: "", url: "",
  }]);
  const save = async () => {
    setSaving(true);
    try {
      await api.post("/admin/calc/lenders", {
        as_of: asOf, lenders: draft.map((r) => ({
          id: r.id, name: r.name, rate: Number(r.rate), foir: Number(r.foir),
          ltv: Number(r.ltv), fee_pct: Number(r.fee_pct),
          fee_max: r.fee_max ? Number(r.fee_max) : null,
          fee_min: r.fee_min ? Number(r.fee_min) : null,
          perks: r.perks || "", url: r.url || "",
        })),
      });
      toast.success(`Saved ${draft.length} lender rows`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };
  const runAutoRefresh = async () => {
    setAutoBusy(true);
    try {
      const { data: preview } = await api.post("/admin/calc/lenders/auto-refresh-preview", {});
      setAutoRefresh(preview);
      // Pre-select all OK lenders with non-trivial delta
      const sel = new Set();
      (preview.lenders || []).forEach((p) => {
        if (p.ok && p.proposed_rate && Math.abs(p.delta || 0) > 0.001) sel.add(p.lender_id);
      });
      setAutoSel(sel);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Auto-refresh preview failed");
    } finally { setAutoBusy(false); }
  };
  const applyAutoRefresh = async () => {
    if (autoSel.size === 0) { toast.error("Select at least one row to apply"); return; }
    const ratesMap = {};
    (autoRefresh?.lenders || []).forEach((p) => {
      if (autoSel.has(p.lender_id) && p.proposed_rate) ratesMap[p.lender_id] = p.proposed_rate;
    });
    const newAsOf = new Date().toISOString().slice(0, 7);
    try {
      await api.post("/admin/calc/lenders/auto-refresh-apply", {
        apply_for: Array.from(autoSel),
        proposed_rates: ratesMap,
        as_of: newAsOf,
      });
      toast.success(`Applied ${autoSel.size} lender rate updates`);
      setAutoRefresh(null);
      setAutoSel(new Set());
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Apply failed");
    }
  };
  if (!data) return <div className="text-xs text-muted-foreground">Loading…</div>;
  return (
    <div className="card-flat p-4 space-y-3" data-testid="lenders-admin-tab">
      <FreshnessHeader label="Lender rates" data={data} threshold="3 months (quarterly)"/>
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs">
          <span className="text-muted-foreground mr-2">As of (YYYY-MM)</span>
          <input value={asOf} onChange={(e) => setAsOf(e.target.value)}
            placeholder="2026-09"
            className="bg-background border hairline px-2 py-1.5 text-xs w-24"
            data-testid="lenders-asof"/>
        </label>
        <button onClick={runAutoRefresh} disabled={autoBusy}
          className="btn-ghost px-2 py-1 text-xs inline-flex items-center gap-1 border border-[hsl(var(--primary))] text-[hsl(var(--primary))]"
          data-testid="lenders-auto-refresh">
          <ArrowsClockwise size={12} className={autoBusy ? "animate-spin" : ""}/>
          {autoBusy ? "Scraping…" : "Auto-refresh from RBI + bank pages"}
        </button>
        <button onClick={addRow} className="btn-ghost px-2 py-1 text-xs inline-flex items-center gap-1"
          data-testid="lenders-add-row">
          <Plus size={12}/>Add lender
        </button>
        <button onClick={save} disabled={saving}
          className="btn-primary px-3 py-1.5 text-xs"
          data-testid="lenders-save">{saving ? "Saving…" : "Save"}</button>
      </div>

      {autoRefresh && (
        <AutoRefreshPreview
          data={autoRefresh}
          sel={autoSel}
          setSel={setAutoSel}
          onClose={() => setAutoRefresh(null)}
          onApply={applyAutoRefresh}
        />
      )}
      <div className="overflow-x-auto border hairline">
        <table className="w-full text-xs">
          <thead className="border-b hairline">
            <tr>
              <th className="p-1.5 text-left">ID</th><th className="p-1.5 text-left">Name</th>
              <th className="p-1.5 text-right">Rate %</th><th className="p-1.5 text-right">FOIR</th>
              <th className="p-1.5 text-right">LTV</th><th className="p-1.5 text-right">Fee %</th>
              <th className="p-1.5 text-right">Fee max</th><th className="p-1.5 text-left">Perks</th>
              <th className="p-1.5"></th>
            </tr>
          </thead>
          <tbody data-testid="lenders-rows">
            {draft.map((r, i) => (
              <tr key={r.id || `new-${i}`} className="border-b hairline">
                <td className="p-1"><input value={r.id || ""} onChange={(e) => updateRow(i, "id", e.target.value)} className="w-16 bg-background border hairline px-1 text-xs"/></td>
                <td className="p-1"><input value={r.name || ""} onChange={(e) => updateRow(i, "name", e.target.value)} className="w-20 bg-background border hairline px-1 text-xs"/></td>
                <td className="p-1"><input type="number" step="0.05" value={r.rate ?? ""} onChange={(e) => updateRow(i, "rate", e.target.value)} className="w-16 bg-background border hairline px-1 text-xs text-right"/></td>
                <td className="p-1"><input type="number" step="0.01" value={r.foir ?? ""} onChange={(e) => updateRow(i, "foir", e.target.value)} className="w-14 bg-background border hairline px-1 text-xs text-right"/></td>
                <td className="p-1"><input type="number" step="0.01" value={r.ltv ?? ""} onChange={(e) => updateRow(i, "ltv", e.target.value)} className="w-14 bg-background border hairline px-1 text-xs text-right"/></td>
                <td className="p-1"><input type="number" step="0.0001" value={r.fee_pct ?? ""} onChange={(e) => updateRow(i, "fee_pct", e.target.value)} className="w-16 bg-background border hairline px-1 text-xs text-right"/></td>
                <td className="p-1"><input type="number" value={r.fee_max ?? ""} onChange={(e) => updateRow(i, "fee_max", e.target.value)} className="w-16 bg-background border hairline px-1 text-xs text-right"/></td>
                <td className="p-1"><input value={r.perks || ""} onChange={(e) => updateRow(i, "perks", e.target.value)} className="w-full bg-background border hairline px-1 text-xs"/></td>
                <td className="p-1">
                  <button onClick={() => removeRow(i)} className="text-red-600 text-xs" data-testid={`lender-remove-${i}`}>
                    <X size={12}/>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-muted-foreground">
        Refresh schedule: <b>quarterly</b> (every RBI MPC cycle). Set <code>as_of</code> to the
        check month (e.g. <code>2026-09</code> for Q3 review). Stale &gt; 3 months will surface a red badge in calculators.
      </div>
    </div>
  );
}

/* ----------------------- CII refresh (annual, July) ---------------------- */
function CiiTab() {
  const [data, setData] = useState(null);
  const [editKey, setEditKey] = useState("");
  const [editVal, setEditVal] = useState("");
  const [asOf, setAsOf] = useState("");
  const load = async () => {
    try {
      const { data } = await api.get("/admin/calc/cii");
      setData(data);
      setAsOf(data.as_of || "");
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);
  const addOrUpdate = async () => {
    if (!editKey || !editVal) { toast.error("Year and CII value required"); return; }
    const current = data?.cii || {};
    const next = { ...current, [String(editKey)]: Number(editVal) };
    try {
      await api.post("/admin/calc/cii", { cii: next, as_of: asOf });
      toast.success(`CII updated for FY ${editKey}-${(Number(editKey) + 1) % 100}`);
      setEditKey(""); setEditVal("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Failed");
    }
  };
  if (!data) return <div className="text-xs text-muted-foreground">Loading…</div>;
  const rows = Object.entries(data.cii || {}).sort((a, b) => Number(a[0]) - Number(b[0]));
  return (
    <div className="card-flat p-4 space-y-3" data-testid="cii-admin-tab">
      <FreshnessHeader label="Cost Inflation Index" data={data} threshold="12 months (July CBDT)"/>
      <div className="flex flex-wrap items-center gap-2">
        <label className="text-xs">
          <span className="text-muted-foreground mr-2">FY start year</span>
          <input type="number" value={editKey} onChange={(e) => setEditKey(e.target.value)}
            placeholder="2026"
            className="bg-background border hairline px-2 py-1 text-xs w-20"
            data-testid="cii-fy"/>
        </label>
        <label className="text-xs">
          <span className="text-muted-foreground mr-2">CII</span>
          <input type="number" value={editVal} onChange={(e) => setEditVal(e.target.value)}
            placeholder="388"
            className="bg-background border hairline px-2 py-1 text-xs w-20"
            data-testid="cii-value"/>
        </label>
        <label className="text-xs">
          <span className="text-muted-foreground mr-2">As of</span>
          <input value={asOf} onChange={(e) => setAsOf(e.target.value)}
            placeholder="2026-07"
            className="bg-background border hairline px-2 py-1 text-xs w-24"
            data-testid="cii-asof"/>
        </label>
        <button onClick={addOrUpdate}
          className="btn-primary px-3 py-1 text-xs"
          data-testid="cii-save">Save row</button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-6 gap-1 text-xs">
        {rows.map(([y, c]) => (
          <div key={y} className="border hairline p-1.5 text-center" data-testid={`cii-row-${y}`}>
            <div className="text-[10px] text-muted-foreground">FY {y}-{(Number(y) + 1) % 100}</div>
            <div className="font-medium">{c}</div>
          </div>
        ))}
      </div>
      <div className="text-[10px] text-muted-foreground">
        Refresh schedule: <b>annual</b>, after CBDT notification (typically June/July). Enter the new
        FY year (e.g. <code>2026</code>) + value (e.g. <code>388</code>), bump <code>as_of</code> to
        e.g. <code>2026-07</code>, click Save.
      </div>
    </div>
  );
}

function FreshnessHeader({ label, data, threshold }) {
  const months = data.months_since_update ?? 0;
  const stale = data.stale;
  const tone = stale
    ? "bg-red-500/10 text-red-700 border-red-500/30"
    : months > Math.floor((Number((data.threshold_months || 12)) * 0.7))
    ? "bg-amber-500/10 text-amber-700 border-amber-500/30"
    : "bg-green-500/10 text-green-700 border-green-500/30";
  return (
    <div className="flex items-baseline justify-between gap-2 flex-wrap">
      <div>
        <div className="text-sm font-medium">{label}</div>
        <div className="text-[11px] text-muted-foreground">
          Refresh threshold: {threshold}.
          {data.next_check && <span className="ml-1">{data.next_check}.</span>}
        </div>
      </div>
      <span className={`px-2 py-0.5 rounded-full text-[11px] border ${tone}`}
        data-testid={`${label.toLowerCase().includes("cii") ? "cii" : "lenders"}-freshness-badge`}>
        {stale ? "⚠ STALE — needs refresh" :
         months <= 0 ? "🟢 Fresh" :
         `🟢 ${data.as_of}  · ${months} month${months === 1 ? "" : "s"} old`}
      </span>
    </div>
  );
}

/* ----------- Auto-refresh preview modal (RBI + bank scraping) ----------- */
function AutoRefreshPreview({ data, sel, setSel, onClose, onApply }) {
  const toggle = (id) => {
    const n = new Set(sel);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSel(n);
  };
  return (
    <div className="border border-[hsl(var(--primary))] p-4 bg-background space-y-3"
      data-testid="auto-refresh-preview">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div>
          <div className="text-sm font-medium">Auto-refresh preview</div>
          <div className="text-[11px] text-muted-foreground">
            Scraped at {(data.scraped_at || "").slice(0, 19)} ·
            RBI repo rate: {data.rbi_repo?.repo_rate_pct != null
              ? <b>{data.rbi_repo.repo_rate_pct}%</b>
              : <span className="text-amber-700">unable to detect</span>}
          </div>
        </div>
        <button onClick={onClose} className="btn-ghost px-2 py-1 text-xs" data-testid="auto-refresh-close">
          <X size={12}/>
        </button>
      </div>
      <table className="w-full text-xs">
        <thead className="border-b hairline">
          <tr>
            <th className="p-1.5 text-left">Apply</th>
            <th className="p-1.5 text-left">Lender</th>
            <th className="p-1.5 text-right">Current</th>
            <th className="p-1.5 text-right">Proposed</th>
            <th className="p-1.5 text-right">Δ</th>
            <th className="p-1.5 text-left">Status</th>
          </tr>
        </thead>
        <tbody data-testid="auto-refresh-rows">
          {(data.lenders || []).map((p) => (
            <tr key={p.lender_id} className="border-b hairline">
              <td className="p-1.5">
                {p.ok && p.proposed_rate != null && (
                  <input type="checkbox" checked={sel.has(p.lender_id)}
                    onChange={() => toggle(p.lender_id)}
                    data-testid={`auto-refresh-check-${p.lender_id}`} />
                )}
              </td>
              <td className="p-1.5"><b>{p.name}</b></td>
              <td className="p-1.5 text-right">{p.current_rate != null ? `${p.current_rate}%` : "—"}</td>
              <td className="p-1.5 text-right">
                {p.proposed_rate != null ? <b>{p.proposed_rate}%</b> : <span className="text-muted-foreground">—</span>}
              </td>
              <td className={`p-1.5 text-right ${
                p.delta == null ? "" : p.delta < 0 ? "text-green-700" : "text-red-700"
              }`}>
                {p.delta != null ? `${p.delta > 0 ? "+" : ""}${p.delta.toFixed(2)}%` : "—"}
              </td>
              <td className="p-1.5 text-[11px]">
                {p.ok ? <span className="text-green-700">✓ scraped</span>
                      : <span className="text-amber-700">⚠ {(p.error || "no data").slice(0, 40)}</span>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div className="text-[10px] text-muted-foreground">
          Scraping is best-effort. Always confirm rates with the bank before applying.
          Configure URLs and regex per-lender in Lender sources (see footer).
        </div>
        <button onClick={onApply}
          disabled={sel.size === 0}
          className="btn-primary px-3 py-1.5 text-xs disabled:opacity-50"
          data-testid="auto-refresh-apply">
          Apply {sel.size} selected
        </button>
      </div>
    </div>
  );
}

/* ----- Freshness digest widget shown at the top of admin panel ----------- */
function DigestWidget() {
  const [status, setStatus] = useState(null);
  const [sending, setSending] = useState(false);
  const load = async () => {
    try {
      const { data } = await api.get("/admin/calc/freshness/digest/status");
      setStatus(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);
  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/admin/calc/freshness/digest/send-now", {});
      if (data?.send_result?.ok) toast.success("Digest email sent");
      else if (data?.send_result?.stub) toast.warning("RESEND_API_KEY not configured — email stubbed");
      else toast.error("Send failed");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Send failed");
    } finally { setSending(false); }
  };
  if (!status) return null;
  const last = status.last_sent_at ? new Date(status.last_sent_at).toLocaleString("en-IN") : "never";
  return (
    <div className="flex items-baseline justify-between gap-2 flex-wrap border-l-4 border-l-[hsl(var(--primary))] bg-[hsl(var(--primary))]/5 p-3"
      data-testid="digest-widget">
      <div>
        <div className="text-sm font-medium">Weekly freshness digest</div>
        <div className="text-[11px] text-muted-foreground">
          Auto-emails to admin every 7 days. Last sent: <b>{last}</b>
          {status.resend_configured
            ? <span className="text-green-700 ml-2">· Resend connected</span>
            : <span className="text-amber-700 ml-2">· RESEND_API_KEY missing — emails stubbed</span>}
        </div>
      </div>
      <button onClick={sendNow} disabled={sending}
        className="btn-primary px-3 py-1.5 text-xs"
        data-testid="digest-send-now">{sending ? "Sending…" : "Send digest now"}</button>
    </div>
  );
}

/* --------------------------- AVM Benchmarks ----------------------------- */
function AvmTab() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [replace, setReplace] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/networth/benchmarks");
      setRows(data.items || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const upload = async () => {
    const f = fileRef.current?.files?.[0];
    if (!f) { toast.error("Pick a CSV first"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post(
        `/admin/networth/benchmarks/upload-csv?replace=${replace}`,
        fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.inserted_or_updated} rows inserted/updated.`);
      if (data.errors?.length) toast.warning(`${data.errors.length} rows skipped — check console.`);
      fileRef.current.value = "";
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Upload failed");
    } finally { setBusy(false); }
  };

  const downloadSample = () => {
    const csv = [
      "city,property_type,price_per_sqft_low,price_per_sqft_mid,price_per_sqft_high,source,updated_month",
      "Mumbai,flat,18000,31500,45000,RERA-NoBroker,2026-06",
      "Bengaluru,villa,12000,21000,30000,RERA-NoBroker,2026-06",
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "estima-benchmark-sample.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="card-flat p-4 space-y-4" data-testid="avm-admin-tab">
      <div className="text-sm font-medium">Upload city × property-type benchmark CSV</div>
      <p className="text-[11px] text-muted-foreground">
        Required columns: <code>city, property_type, price_per_sqft_low,
        price_per_sqft_high</code>. Optional: <code>price_per_sqft_mid, source, updated_month</code>.
        <code>property_type</code> must be one of <code>flat / villa / plot / commercial</code>.
        Existing rows for the same city + type are updated in place; new rows are appended.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <input type="file" accept=".csv" ref={fileRef}
          className="text-xs"
          data-testid="avm-csv-file" />
        <label className="flex items-center gap-1 text-xs">
          <input type="checkbox" checked={replace} onChange={(e) => setReplace(e.target.checked)}
            data-testid="avm-csv-replace" />
          Replace all existing rows
        </label>
        <button onClick={upload} disabled={busy}
          className="btn-primary px-3 py-1.5 text-xs inline-flex items-center gap-1 disabled:opacity-50"
          data-testid="avm-csv-upload-btn">
          <Upload size={12} weight="bold" />{busy ? "Uploading…" : "Upload CSV"}
        </button>
        <button onClick={downloadSample}
          className="btn-ghost px-3 py-1.5 text-xs"
          data-testid="avm-csv-sample">
          Download sample CSV
        </button>
      </div>

      <div className="mt-4 max-h-[360px] overflow-y-auto border hairline">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-background border-b hairline">
            <tr>
              <th className="text-left p-2">City</th>
              <th className="text-left p-2">Type</th>
              <th className="text-right p-2">Low</th>
              <th className="text-right p-2">Mid</th>
              <th className="text-right p-2">High</th>
              <th className="text-left p-2">Source</th>
              <th className="text-left p-2">Updated</th>
            </tr>
          </thead>
          <tbody data-testid="avm-rows">
            {rows.map((r) => (
              <tr key={r.id} className="border-b hairline">
                <td className="p-2">{r.city}</td>
                <td className="p-2 capitalize">{r.property_type}</td>
                <td className="p-2 text-right">₹{r.price_per_sqft_low?.toLocaleString("en-IN")}</td>
                <td className="p-2 text-right">₹{r.price_per_sqft_mid?.toLocaleString("en-IN")}</td>
                <td className="p-2 text-right">₹{r.price_per_sqft_high?.toLocaleString("en-IN")}</td>
                <td className="p-2 text-muted-foreground">{r.source}</td>
                <td className="p-2 text-muted-foreground">{r.updated_month}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-muted-foreground">{rows.length} rows.</div>
    </div>
  );
}

/* ----------------------- Property History config ------------------------- */
function PropertyHistoryTab() {
  const [cfg, setCfg] = useState(null);
  const [apiKey, setApiKey] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/property-history/config");
      setCfg(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const save = async (patch) => {
    setSaving(true);
    try {
      await api.post("/admin/property-history/config", patch);
      toast.success("Saved.");
      await load();
      setApiKey("");
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  if (!cfg) return <div className="text-xs text-muted-foreground">Loading…</div>;

  return (
    <div className="card-flat p-4 space-y-4" data-testid="ph-admin-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="text-sm font-medium">Property history feature</div>
          <p className="text-[11px] text-muted-foreground mt-1 max-w-lg">
            When enabled, search results come from your configured API provider
            (Karza / digiverification). When disabled OR no key, the UI returns
            mock data with a &quot;Demo mode&quot; banner.
          </p>
        </div>
        <button
          onClick={() => save({ enabled: !cfg.enabled })}
          disabled={saving}
          className={`px-4 py-1.5 text-xs rounded ${cfg.enabled ? "bg-[hsl(var(--primary))] text-white" : "border hairline"}`}
          data-testid="ph-enabled-toggle">
          {cfg.enabled ? "Enabled · click to disable" : "Disabled · click to enable"}
        </button>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-xs">
          <div className="font-medium mb-1">Provider</div>
          <select value={cfg.provider} onChange={(e) => save({ provider: e.target.value })}
            className="w-full bg-background border hairline px-2 py-1.5"
            data-testid="ph-provider">
            <option value="karza">Karza Technologies</option>
            <option value="digiverification">digiverification.com</option>
          </select>
        </label>
        <div className="text-xs">
          <div className="font-medium mb-1">API key</div>
          <div className="flex items-center gap-2">
            <input value={apiKey} onChange={(e) => setApiKey(e.target.value)}
              placeholder={cfg.has_api_key ? `Current: ${cfg.api_key_masked}` : "Paste your provider API key…"}
              className="flex-1 bg-background border hairline px-2 py-1.5"
              data-testid="ph-api-key" />
            <button onClick={() => save({ api_key: apiKey })} disabled={saving || !apiKey}
              className="btn-primary px-3 py-1.5 text-xs disabled:opacity-50"
              data-testid="ph-api-key-save">Save</button>
            {cfg.has_api_key && (
              <button onClick={() => save({ api_key: "" })} disabled={saving}
                className="btn-ghost px-3 py-1.5 text-xs"
                data-testid="ph-api-key-clear">Clear</button>
            )}
          </div>
          <div className="text-[10px] text-muted-foreground mt-1">
            Stored encrypted at rest. Replace freely — change takes effect on next search.
          </div>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-xs">
          <div className="font-medium mb-1">Free tier — searches/day</div>
          <input type="number" defaultValue={cfg.free_limit_per_day}
            onBlur={(e) => save({ free_limit_per_day: Number(e.target.value) || 3 })}
            className="w-full bg-background border hairline px-2 py-1.5"
            data-testid="ph-free-limit" />
        </label>
        <label className="text-xs">
          <div className="font-medium mb-1">Pro tier — searches/day</div>
          <input type="number" defaultValue={cfg.pro_limit_per_day}
            onBlur={(e) => save({ pro_limit_per_day: Number(e.target.value) || 100 })}
            className="w-full bg-background border hairline px-2 py-1.5"
            data-testid="ph-pro-limit" />
        </label>
      </div>

      <div className="border border-amber-500/30 bg-amber-500/10 text-amber-700 text-[11px] p-3"
        data-testid="ph-legal-reminder">
        <b>Before enabling:</b> ensure (1) legal opinion confirming IGRS aggregation is OK under
        Registration Act 1908 + DPDP Act 2023, (2) signed Data Sharing Agreement with your
        provider, (3) updated Privacy Policy + ToS published on estima.co.in.
      </div>
    </div>
  );
}

/* --------------------------- Search log --------------------------------- */
function SearchesTab() {
  const [items, setItems] = useState([]);
  const load = async () => {
    try {
      const { data } = await api.get("/admin/property-history/searches?limit=100");
      setItems(data.items || []);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);
  return (
    <div className="card-flat p-4 space-y-3" data-testid="ph-searches-tab">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium">Recent property history searches</div>
        <button onClick={load} className="btn-ghost px-2 py-1 text-xs inline-flex items-center gap-1">
          <ArrowsClockwise size={12} /> Refresh
        </button>
      </div>
      <div className="max-h-[420px] overflow-y-auto border hairline">
        <table className="w-full text-xs">
          <thead className="sticky top-0 bg-background border-b hairline">
            <tr>
              <th className="text-left p-2">When</th>
              <th className="text-left p-2">User</th>
              <th className="text-left p-2">State / District</th>
              <th className="text-left p-2">Property</th>
              <th className="text-right p-2">Txns</th>
              <th className="text-left p-2">Mode</th>
            </tr>
          </thead>
          <tbody>
            {items.map((s) => (
              <tr key={s.id} className="border-b hairline">
                <td className="p-2 text-muted-foreground">{(s.searched_at || "").slice(0, 19).replace("T", " ")}</td>
                <td className="p-2 truncate max-w-[160px]">{(s.user_id || "").slice(-8)}</td>
                <td className="p-2">{s.search_params?.state} / {s.search_params?.district}</td>
                <td className="p-2 truncate max-w-[200px]">{s.search_params?.property_identifier}</td>
                <td className="p-2 text-right">{s.n_transactions}</td>
                <td className="p-2">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${
                    s.mode === "live"
                      ? "bg-green-500/15 text-green-700"
                      : "bg-amber-500/15 text-amber-700"
                  }`}>{s.mode}</span>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr><td colSpan={6} className="p-3 text-center text-muted-foreground">No searches yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
